import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/Layout/Layout';
import SideBar from '@/components/SideBar/SideBar';
import api from '@/shared/api/api';
import { useFetch } from '@/shared/hooks/useFetch';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@/shared/ui/Buttons/Button';
import useModals from '@/shared/hooks/useModal';
import LeftSideModal from '@/shared/ui/Modals/LeftSideModal/LeftSideModal';
import TestRunForm from '../test_runs/form/TestRunForm';
import {
    TestCaseGroupCreateDTO,
    TestCaseGroupResponseDTO,
    TestCaseListResponseDTO,
    TestCaseStatus,
    TestRunCreateDTO,
} from '@/shared/api/gen/data-contracts';
import ProjectSidebar from './components/ProjectSidebar';
import { TestCaseGroupAccordion } from './components/TestCaseGroupAccordion';
import { TestCaseGroupForm } from './components/TestCaseGroupForm';

const PRIORITIES = [1, 2, 3, 4, 5];

function ProjectManage() {
    const { openModal, closeModal } = useModals();
    const { projectID } = useParams();
    const navigate = useNavigate();

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [priorityFilter, setPriorityFilter] = useState<number | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const { data: testCasesRaw, refresh: refreshCases } = useFetch(
        () => api.test_cases.getTestcasesApiTestCasesGet({
            project_ref: Number(projectID),
            order_by: 'priority_desc',
            search: debouncedSearch || undefined,
            priority: priorityFilter ?? undefined,
        }),
        [], [projectID, debouncedSearch, priorityFilter], { autoRefresh: true, refreshTiming: 80 }
    );

    const { data: groupsRaw, refresh: refreshGroups } = useFetch(
        () => api.test_cases.getTestCaseGroupsApiTestCasesGroupsGet({ project_ref: Number(projectID) }),
        [], [projectID]
    );

    const activeCases: TestCaseListResponseDTO[] = (testCasesRaw as TestCaseListResponseDTO[])?.filter(
        (tc) => tc.status !== TestCaseStatus.DEPRICATED
    ) ?? [];

    const groups: TestCaseGroupResponseDTO[] = (groupsRaw as TestCaseGroupResponseDTO[]) ?? [];

    const casesByGroup = new Map<number, TestCaseListResponseDTO[]>();
    const ungroupedCases: TestCaseListResponseDTO[] = [];

    activeCases.forEach((tc) => {
        if (tc.group_ref) {
            if (!casesByGroup.has(tc.group_ref)) casesByGroup.set(tc.group_ref, []);
            casesByGroup.get(tc.group_ref)!.push(tc);
        } else {
            ungroupedCases.push(tc);
        }
    });

    const archiveCase = async (id: number) => {
        await api.test_cases.updateTestcaseStatusApiTestCasesIdStatusPatch(id, { status: TestCaseStatus.DEPRICATED });
        refreshCases();
    };

    const moveToGroup = async (caseId: number, groupId: number | null) => {
        await api.test_cases.updateTestcaseGroupApiTestCasesIdGroupPatch(caseId, { group_ref: groupId });
        refreshCases();
    };

    const createTestRun = async (data: TestRunCreateDTO) => {
        await api.test_runs.createTestRunApiTestRunsPost(data);
        closeModal();
        navigate(`/projects/manage/${projectID}/test_runs`);
    };

    const openTestRunCreationForm = () => {
        openModal(
            <LeftSideModal>
                <TestRunForm
                    onSubmit={createTestRun}
                    defaultData={{ project_ref: Number(projectID) }}
                    projectID={Number(projectID)}
                    onCancel={closeModal}
                />
            </LeftSideModal>
        );
    };

    const openGroupCreateForm = () => {
        openModal(
            <LeftSideModal>
                <TestCaseGroupForm
                    projectRef={Number(projectID)}
                    onSubmit={async (data: TestCaseGroupCreateDTO) => {
                        await api.test_cases.createTestCaseGroupApiTestCasesGroupsPost(data);
                        refreshGroups();
                        closeModal();
                    }}
                    onCancel={closeModal}
                />
            </LeftSideModal>
        );
    };

    return (
        <AdminLayout
            sidebarSlot={
                <SideBar>
                    <ProjectSidebar id={projectID} />
                </SideBar>
            }
        >
            <div style={{ display: 'flex', gap: '12px', alignSelf: 'flex-end' }}>
                <Button
                    style={{ minHeight: '50px' }}
                    className='text-15 cursor-pointer normal'
                    onClick={openGroupCreateForm}
                >
                    Создать группу
                </Button>
                <Button
                    style={{ minHeight: '50px' }}
                    className='text-15 cursor-pointer normal'
                    onClick={() => navigate('/test_cases/create')}
                >
                    Создать тест-кейс
                </Button>
                <Button
                    style={{ minHeight: '50px' }}
                    className='text-15 cursor-pointer normal'
                    onClick={openTestRunCreationForm}
                >
                    Создать тестовый прогон
                </Button>
            </div>

            <h1 className='text-30' style={{ width: '100%' }}>Тест-кейсы</h1>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Поиск по названию, описанию, тегам..."
                    style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                    }}
                />
                <div style={{ display: 'flex', gap: '6px' }}>
                    {PRIORITIES.map(p => (
                        <button
                            key={p}
                            onClick={() => setPriorityFilter(prev => prev === p ? null : p)}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '12px',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 500,
                                backgroundColor: priorityFilter === p ? '#7A5ACD' : '#e5e7eb',
                                color: priorityFilter === p ? '#fff' : '#374151',
                                transition: 'background 0.15s',
                            }}
                        >
                            P{p}
                        </button>
                    ))}
                </div>
            </div>

            <div className="GroupList">
                {groups.map((group) => (
                    <TestCaseGroupAccordion
                        key={group.id}
                        group={group}
                        groups={groups}
                        testCases={casesByGroup.get(group.id) ?? []}
                        onArchive={archiveCase}

                        onMoveToGroup={moveToGroup}
                    />
                ))}

                {ungroupedCases.length > 0 && (
                    <TestCaseGroupAccordion
                        key="ungrouped"
                        group={{ id: null, title: 'Без группы' }}
                        groups={groups}
                        testCases={ungroupedCases}
                        onArchive={archiveCase}

                        onMoveToGroup={moveToGroup}
                    />
                )}
            </div>
        </AdminLayout>
    );
}

export default ProjectManage;
